import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CdkDragEnd, DragDropModule } from '@angular/cdk/drag-drop';
import {
  switchMap,
  takeUntil,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { ImageScanLoaderComponent } from './image-scan-loader/image-scan-loader.component';
import { OsintSearchService } from '../../../services/osint-search/osint-search.service';
import { navigateToOsintImageSearch } from '../../../services/osint-search/image-search-from-result.util';

@Component({
  selector: 'app-osint-image-search-result',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    SidebarComponent,
    FormsModule,
    ImageScanLoaderComponent,
    DragDropModule,
  ],
  templateUrl: './osint-image-search-result.component.html',
  styleUrls: ['./osint-image-search-result.component.css'],
})
export class OsintImageSearchResultComponent implements OnInit, OnDestroy {
  jobId: string = '';
  allImagesLoaded: boolean = false;
  pollDuration = 5 * 60 * 1000;
  pollInterval = 5 * 1000;

  showSearchNameEditInput: boolean = false;
  searchName: string = 'Face Search';
  searchText: string = '';
  selectedFilter: 'all' | 'high' | 'low' = 'all';
  flattenContexts: any[] = [];
  highProbabilityThreshold: number = 0.94;

  selectedImageDetails: any;

  percentageComplete: string = 'Loading...';
  searchResults: any[] = [];
  searchResultsFiltered: any[] = [];

  showSelectedResult: boolean = false;
  selectedResult: any = null;
  selectedResultIndex: number = -1;
  contextsMatrix: any[] = [];

  noImageFoundPath: string = 'assets/images/no-image-found.png';

  /** Draggable query-face thumbnail (face1 img_man) */
  dragPosition = { x: 0, y: 0 };

  private imagesReady = new Set<string>();

  profileCards?: ElementRef<HTMLElement>;
  private masonryGap = 8;
  private masonryRaf: number | null = null;
  private masonryResizeObserver?: ResizeObserver;

  @ViewChild('profileCards')
  set profileCardsRef(ref: ElementRef<HTMLElement> | undefined) {
    this.profileCards = ref;
    this.masonryResizeObserver?.disconnect();
    if (ref?.nativeElement && typeof ResizeObserver !== 'undefined') {
      this.masonryResizeObserver = new ResizeObserver(() =>
        this.scheduleMasonryLayout(),
      );
      this.masonryResizeObserver.observe(ref.nativeElement);
      this.scheduleMasonryLayout();
    }
  }

  constructor(
    private router: Router,
    private osintSearchService: OsintSearchService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    const staleSearchId = history.state?.staleSearchId;
    const isStaleResult = history.state?.isStaleResult;

    if (isStaleResult && staleSearchId) {
      this.selectedImageDetails = history.state?.selectedImageDetails;
      this.jobId = history.state?.jobId || '';
      this.searchName = this.resolveSearchName(history.state?.searchName);
      this.getStaleSearchResults(staleSearchId);
    } else {
      this.selectedImageDetails = history.state?.selectedImageDetails;
      this.searchName = this.resolveSearchName(
        history.state?.searchName ||
          this.selectedImageDetails?.originalFileName,
      );
      this.getSearchResults();
    }
  }

  ngOnDestroy(): void {
    this.imagesReady.clear();
    this.masonryResizeObserver?.disconnect();
    if (this.masonryRaf != null) {
      cancelAnimationFrame(this.masonryRaf);
      this.masonryRaf = null;
    }
  }

  onBackButtonClick() {
    this.router.navigate(['/osint-image-search']);
  }

  getSearchResults() {
    this.allImagesLoaded = false;
    this.percentageComplete = 'Loading...';
    this.imagesReady.clear();

    const payload = {
      fid: this.selectedImageDetails?.fid,
      thumbnail: this.selectedImageDetails?.thumbnail,
      job_name: this.searchName,
    };

    this.osintSearchService.getImageSearchJobId(payload).subscribe({
      next: (response) => {
        this.jobId = response?.data?.jid;

        timer(0, this.pollInterval)
          .pipe(
            switchMap(() =>
              this.osintSearchService.getImageSearchResult(this.jobId),
            ),
            tap((response) => {
              if (response?.data?.progress !== undefined) {
                this.percentageComplete = `${response?.data?.progress}%`;
              } else if (!response?.data?.result) {
                this.percentageComplete = 'Loading...';
              }
            }),
            takeWhile((response) => !response?.data?.result, true),
            takeUntil(timer(this.pollDuration)),
          )
          .subscribe({
            next: (response) => {
              if (response?.data?.result?.length > 0) {
                this.searchResults = response.data.result;
                this.searchResultsByProbability();
                this.createFlattenContexts(response.data.result);
                this.onSearchResultsVisualReady();
              } else if (response?.data?.result) {
                this.searchResults = [];
                this.searchResultsFiltered = [];
                this.allImagesLoaded = true;
              }
            },
            error: (error) => {
              console.error(error);
              this.allImagesLoaded = true;
            },
          });
      },
      error: (error) => {
        console.error(error);
        this.allImagesLoaded = true;

        const errorMessage =
          error?.error?.message_key ||
          error?.error?.message ||
          error?.message ||
          error?.statusText ||
          'An error occurred while processing your request';

        this.toastr.error(errorMessage, 'Search Failed');
        this.router.navigate(['/osint-image-search']);
      },
    });
  }

  private matchProbabilityNumeric(item: unknown): number {
    const v = parseFloat(String((item as any)?.match_probability ?? ''));
    return Number.isFinite(v) ? v : 0;
  }

  searchResultsByProbability() {
    if (this.hasHighProbability()) {
      this.filterByHighProbability();
      this.selectedFilter = 'high';
    } else {
      this.filterByLowProbability();
      this.selectedFilter = 'low';
    }
  }

  hasHighProbability() {
    return (this.searchResults || []).some(
      (searchResult: any) =>
        this.matchProbabilityNumeric(searchResult) >=
        this.highProbabilityThreshold,
    );
  }

  filterByAllProbability() {
    this.searchResultsFiltered = [...(this.searchResults || [])].sort(
      (a: any, b: any) =>
        this.matchProbabilityNumeric(b) - this.matchProbabilityNumeric(a),
    );
    this.scheduleMasonryLayout();
  }

  filterByHighProbability() {
    this.searchResultsFiltered = (this.searchResults || [])
      .filter(
        (searchResult: any) =>
          this.matchProbabilityNumeric(searchResult) >=
          this.highProbabilityThreshold,
      )
      .sort(
        (a: any, b: any) =>
          this.matchProbabilityNumeric(b) - this.matchProbabilityNumeric(a),
      );
    this.scheduleMasonryLayout();
  }

  filterByLowProbability() {
    this.searchResultsFiltered = (this.searchResults || [])
      .filter(
        (searchResult: any) =>
          this.matchProbabilityNumeric(searchResult) <
          this.highProbabilityThreshold,
      )
      .sort(
        (a: any, b: any) =>
          this.matchProbabilityNumeric(b) - this.matchProbabilityNumeric(a),
      );
    this.scheduleMasonryLayout();
  }

  onAllButtonClick() {
    this.filterByAllProbability();
    this.selectedFilter = 'all';
  }

  onHighButtonClick() {
    this.filterByHighProbability();
    this.selectedFilter = 'high';
  }

  onLowButtonClick() {
    this.filterByLowProbability();
    this.selectedFilter = 'low';
  }

  openSelectedResult(item: any, index?: number) {
    this.selectedResult = item;
    this.showSelectedResult = true;

    if (
      typeof index === 'number' &&
      index >= 0 &&
      index < this.searchResultsFiltered.length &&
      this.searchResultsFiltered[index] === item
    ) {
      this.selectedResultIndex = index;
    } else {
      const byRef = this.searchResultsFiltered.indexOf(item);
      this.selectedResultIndex =
        byRef >= 0
          ? byRef
          : this.searchResultsFiltered.findIndex(
              (r: any) => this.resultTrackId(r) === this.resultTrackId(item),
            );
    }

    this.createContextMatrix();
  }

  closeSelectedResult() {
    this.showSelectedResult = false;
    this.selectedResultIndex = -1;
    this.dragPosition = { x: 0, y: 0 };
  }

  get canShowPreviousResult(): boolean {
    return (
      this.showSelectedResult &&
      this.selectedResultIndex > 0 &&
      this.searchResultsFiltered.length > 1
    );
  }

  get canShowNextResult(): boolean {
    return (
      this.showSelectedResult &&
      this.selectedResultIndex >= 0 &&
      this.selectedResultIndex < this.searchResultsFiltered.length - 1
    );
  }

  showPreviousResult(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (!this.canShowPreviousResult) return;
    const index = this.selectedResultIndex - 1;
    this.openSelectedResult(this.searchResultsFiltered[index], index);
  }

  showNextResult(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (!this.canShowNextResult) return;
    const index = this.selectedResultIndex + 1;
    this.openSelectedResult(this.searchResultsFiltered[index], index);
  }

  onDragEnd(event: CdkDragEnd): void {
    const { x, y } = event.source.getFreeDragPosition();
    this.dragPosition = { x, y };
  }

  get queryFaceThumbnail(): string {
    return this.selectedImageDetails?.thumbnail || this.noImageFoundPath;
  }

  trackBySearchResultIdentity = (_index: number, result: any): string =>
    this.resultTrackId(result) || `face-result-${_index}`;

  createContextMatrix() {
    this.contextsMatrix = (
      Array.isArray(this.selectedResult?.contexts)
        ? this.selectedResult.contexts
        : []
    )
      .filter(
        (context: any) =>
          context && typeof context === 'object' && !Array.isArray(context),
      )
      .map((context: any) =>
        Object.entries(context).map(([key, value]) => ({
          key: this.formatKey(key),
          value,
        })),
      );
  }

  formatKey(key: string) {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  isUrl(url: string) {
    const urlPattern = /^(https?:\/\/)?([\w\-]+\.)+[\w-]{2,}(\:\d+)?(\/.*)?$/i;
    return typeof url === 'string' && urlPattern.test(url);
  }

  private resolveSearchName(name: string | null | undefined): string {
    const trimmed = String(name || '').trim();
    if (!trimmed || trimmed.toLowerCase() === 'new search') {
      return 'Face Search';
    }
    return trimmed;
  }

  editSearchName() {
    if (this.showSearchNameEditInput) {
      this.saveSearchName();
    } else {
      this.showSearchNameEditInput = true;
    }
  }

  saveSearchName() {
    if (!this.jobId || !this.searchName.trim()) {
      this.showSearchNameEditInput = false;
      return;
    }

    const payload = {
      job_name: this.searchName.trim().slice(0, 50),
    };

    this.osintSearchService
      .updateImageSearchHistory(this.jobId, payload)
      .subscribe({
        next: () => {
          this.showSearchNameEditInput = false;
        },
        error: (error) => {
          console.error('UPDATE SEARCH HISTORY ERROR: ', error);
          this.showSearchNameEditInput = false;
        },
      });
  }

  createFlattenContexts(items: any) {
    this.flattenContexts = (items || []).map((item: any) => ({
      identity: item?.identity,
      flattenContext: (Array.isArray(item?.contexts) ? item.contexts : [])
        .filter(
          (context: any) =>
            context && typeof context === 'object' && !Array.isArray(context),
        )
        .map((context: any) =>
          Object.entries(context)
            .map(([key, value]) => `${key} ${value}`)
            .join(' '),
        )
        .join(' '),
    }));
  }

  onSearchTextChange() {
    if (this.searchText.length === 0) {
      if (this.selectedFilter === 'all') {
        this.filterByAllProbability();
      }
    }
  }

  onSearchTextKeydownEnter() {
    if (this.searchText.length > 0) {
      const filteredFlattenContexts = this.flattenContexts.filter((item: any) =>
        item.flattenContext
          .toLowerCase()
          .includes(this.searchText.toLowerCase()),
      );

      this.searchResultsFiltered = this.searchResults
        .filter((searchResult: any) =>
          filteredFlattenContexts.some(
            (filteredFlattenContext: any) =>
              searchResult?.identity === filteredFlattenContext?.identity,
          ),
        )
        .sort(
          (a: any, b: any) =>
            this.matchProbabilityNumeric(b) - this.matchProbabilityNumeric(a),
        );

      this.selectedFilter = 'all';
      this.scheduleMasonryLayout();
    }
  }

  onImageError(event: Event, searchResult?: any) {
    if (searchResult) {
      searchResult._imageError = true;
      // Keep a portrait placeholder ratio so layout still works
      if (!searchResult._naturalWidth) {
        searchResult._naturalWidth = 3;
        searchResult._naturalHeight = 4;
      }
      this.markImageReady(searchResult);
    }
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = this.noImageFoundPath;
    }
    this.cdr.detectChanges();
    this.scheduleMasonryLayout();
  }

  onImageLoad(event: Event, searchResult?: any) {
    const img = event.target as HTMLImageElement;
    if (searchResult && img?.naturalWidth > 0 && img?.naturalHeight > 0) {
      searchResult._naturalWidth = img.naturalWidth;
      searchResult._naturalHeight = img.naturalHeight;
    }
    if (searchResult) {
      this.markImageReady(searchResult);
    }
    this.cdr.detectChanges();
    this.scheduleMasonryLayout();
  }

  resultTrackId(result: any): string {
    return String(
      result?.result_id ||
        result?.identity ||
        result?.image_url ||
        result?.display_image_url ||
        '',
    );
  }

  isImageReady(result: any): boolean {
    const id = this.resultTrackId(result);
    return !id || this.imagesReady.has(id) || Boolean(result?._imageError);
  }

  private markImageReady(result: any): void {
    const id = this.resultTrackId(result);
    if (id) {
      this.imagesReady.add(id);
    }
  }

  /** Justified rows: fixed height, widths follow aspect ratio (same as face1). */
  scheduleMasonryLayout(): void {
    if (this.masonryRaf != null) {
      cancelAnimationFrame(this.masonryRaf);
    }
    this.masonryRaf = requestAnimationFrame(() => {
      this.masonryRaf = null;
      this.layoutMasonry();
    });
  }

  private getJustifiedRowHeight(containerWidth: number): number {
    if (containerWidth <= 576) return 140;
    if (containerWidth <= 992) return 180;
    if (containerWidth >= 1400) return 240;
    return 220;
  }

  private getCardAspectRatio(card: HTMLElement, index: number): number {
    const id = card.getAttribute('data-result-id');
    const list = this.searchResultsFiltered || [];
    const result =
      (id ? list.find((r: any) => this.resultTrackId(r) === id) : null) ||
      list[index];

    if (result?._naturalWidth > 0 && result?._naturalHeight > 0) {
      return result._naturalWidth / result._naturalHeight;
    }

    const img = card.querySelector(
      'img.result-card-image',
    ) as HTMLImageElement | null;
    const naturalWidth = img?.naturalWidth ?? 0;
    const naturalHeight = img?.naturalHeight ?? 0;
    if (naturalWidth > 0 && naturalHeight > 0) {
      return naturalWidth / naturalHeight;
    }

    return 3 / 4;
  }

  private layoutMasonry(): void {
    const container = this.profileCards?.nativeElement;
    if (!container) return;

    const cards = Array.from(
      container.querySelectorAll(':scope > .result-card'),
    ) as HTMLElement[];

    if (!cards.length) {
      container.style.height = '';
      return;
    }

    const gap = this.masonryGap;
    const containerWidth = container.clientWidth;
    if (containerWidth <= 0) return;

    const targetHeight = this.getJustifiedRowHeight(containerWidth);
    const items = cards.map((card, index) => ({
      card,
      ratio: this.getCardAspectRatio(card, index),
    }));

    type RowItem = (typeof items)[number];
    let row: RowItem[] = [];
    let rowAspectSum = 0;
    let y = 0;

    const placeRow = (itemsInRow: RowItem[], rowHeight: number) => {
      let x = 0;
      for (const item of itemsInRow) {
        const width = item.ratio * rowHeight;
        item.card.style.position = 'absolute';
        item.card.style.margin = '0';
        item.card.style.left = `${x}px`;
        item.card.style.top = `${y}px`;
        item.card.style.width = `${width}px`;
        item.card.style.height = `${rowHeight}px`;
        item.card.style.transform = '';
        x += width + gap;
      }
      y += rowHeight + gap;
    };

    const flushRow = (itemsInRow: RowItem[], stretch: boolean) => {
      if (!itemsInRow.length) return;
      const gapsWidth = gap * Math.max(itemsInRow.length - 1, 0);
      const aspectSum = itemsInRow.reduce((sum, item) => sum + item.ratio, 0);

      let rowHeight: number;
      const idealWidth = aspectSum * targetHeight + gapsWidth;
      if (!stretch && idealWidth < containerWidth * 0.9) {
        rowHeight = targetHeight;
      } else {
        rowHeight = (containerWidth - gapsWidth) / aspectSum;
      }

      placeRow(itemsInRow, rowHeight);
    };

    for (const item of items) {
      row.push(item);
      rowAspectSum += item.ratio;
      const rowWidthAtTarget =
        rowAspectSum * targetHeight + gap * (row.length - 1);

      if (rowWidthAtTarget > containerWidth) {
        if (row.length === 1) {
          flushRow(row, true);
          row = [];
          rowAspectSum = 0;
        } else {
          const overflowItem = row.pop()!;
          rowAspectSum -= overflowItem.ratio;
          flushRow(row, true);
          row = [overflowItem];
          rowAspectSum = overflowItem.ratio;
          if (rowAspectSum * targetHeight > containerWidth) {
            flushRow(row, true);
            row = [];
            rowAspectSum = 0;
          }
        }
      }
    }

    flushRow(row, false);
    container.style.height = `${Math.max(y - gap, 0)}px`;
  }

  getAllCount(): number {
    return this.searchResults?.length || 0;
  }

  getHighCount(): number {
    if (!this.searchResults?.length) return 0;
    return this.searchResults.filter(
      (item: any) =>
        this.matchProbabilityNumeric(item) >= this.highProbabilityThreshold,
    ).length;
  }

  getLowCount(): number {
    if (!this.searchResults?.length) return 0;
    return this.searchResults.filter(
      (item: any) =>
        this.matchProbabilityNumeric(item) < this.highProbabilityThreshold,
    ).length;
  }

  getImageSource(result: any): string {
    if (result?._imageError) {
      return this.noImageFoundPath;
    }

    return (
      result?.display_image_url ||
      result?.blobUrl ||
      result?.image_url ||
      result?.thumbnail ||
      this.noImageFoundPath
    );
  }

  canStartImageSearch(result?: any): boolean {
    if (!result || result._imageError) {
      return false;
    }

    const src = this.getImageSource(result);
    return !!src && src !== this.noImageFoundPath;
  }

  startImageSearchFromResult(result: any, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canStartImageSearch(result)) {
      return;
    }

    const src = this.getImageSource(result);
    if (!src) {
      return;
    }

    void navigateToOsintImageSearch(this.router, src).catch(() => {
      this.toastr.error('Unable to start Image Search', 'Search Failed');
    });
  }

  private onSearchResultsVisualReady(): void {
    this.allImagesLoaded = true;
    this.cdr.detectChanges();
    // Wait a tick so ViewChild #profileCards exists after *ngIf
    setTimeout(() => this.scheduleMasonryLayout(), 0);
  }

  getStaleSearchResults(searchId: string) {
    this.allImagesLoaded = false;
    this.percentageComplete = 'Loading...';
    this.imagesReady.clear();

    this.osintSearchService
      .getImageSearchStaleResults(searchId, 1, 1000)
      .subscribe({
        next: (response) => {
          const dataList = Array.isArray(response?.data?.data_list)
            ? response.data.data_list
            : [];
          this.searchResults = dataList;
          this.searchResultsFiltered = [...dataList];
          try {
            this.createFlattenContexts(this.searchResults);
            this.searchResultsByProbability();
          } catch (error) {
            console.error('Unable to prepare stale face search results', error);
            this.filterByAllProbability();
            this.selectedFilter = 'all';
          }
          this.onSearchResultsVisualReady();
        },
        error: (error) => {
          console.error('GET STALE SEARCH RESULTS ERROR: ', error);
          this.allImagesLoaded = true;
        },
      });
  }
}
